import React, { useState } from 'react';
import { Box, Button, Icon, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile'

import DialogBase from './DialogBase';

const MAX_FILE_SIZE_BYTES = 150 * 1024 * 1024;
const GEO_PACKAGE_MIME_TYPES = new Set([
  'application/geopackage+sqlite3',
  'application/x-sqlite3',
  'application/vnd.sqlite3',
]);

interface DialogUploadGeoPackageProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (file: File) => void;
}

const DialogUploadGeoPackage: React.FC<DialogUploadGeoPackageProps> = ({
  open,
  title,
  onClose,
  onSubmit,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isGeoPackageFile = (file: File) => {
    const hasGpkgExtension = file.name.toLowerCase().endsWith('.gpkg');
    const hasGeoPackageMimeType = GEO_PACKAGE_MIME_TYPES.has(file.type.toLowerCase());

    return hasGpkgExtension || hasGeoPackageMimeType;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (isGeoPackageFile(file)) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          setError('Bestand is te groot. De toegestane maximale bestandsgrootte is 150MB.');
          setSelectedFile(null);
          return;
        }

        setSelectedFile(file);
        setError(null);
      } 
      else {
        setError('Alleen GeoPackage-bestanden zijn toegestaan (.gpkg of geldig MIME-type).');
        setSelectedFile(null);
      }
    }
  };

  const handleSubmit = () => {
    if (error) {
      return;
    }

    if (!selectedFile) {
      setError('Selecteer een GeoPackage (.gpkg) bestand.');
      return;
    }
    onSubmit(selectedFile);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    onClose();
  };

  return (
    <DialogBase
      open={open}
      title={title}
      contentText={`Selecteer een GeoPackage (.gpkg) bestand om te uploaden.<br/> De toegestane maximale bestandsgrootte is ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB.`}
      onClose={handleClose}
      onConfirm={handleSubmit}
      confirmButtonText="Uploaden"
      confirmButtonIcon={<Icon>upload</Icon>}
      confirmButtonDisabled={error ? true : null}
    >
      <Box mt={2}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileIcon />}
        >
          Kies bestand
          <input
            type="file"
            hidden
            accept=".gpkg,application/geopackage+sqlite3,application/x-sqlite3,application/vnd.sqlite3"
            onChange={handleFileChange}
          />
        </Button>

        {selectedFile && (
          <Typography mt={1}>
            Geselecteerd: <strong>{selectedFile.name}</strong>
          </Typography>
        )}

        {error && (
          <Typography color="error" mt={1}>
            {error}
          </Typography>
        )}
      </Box>
    </DialogBase>
  );
};

export default DialogUploadGeoPackage;
